import 'dayjs/locale/ja'

import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Drawer,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { MouseEvent, useCallback, useEffect, useState } from 'react'
import { Icons, SkeletonText } from 'utoniq-ui'

import {
  Artist,
  ArtistPaymentType,
  Maybe,
  OrderBy,
  UserCartTransactionOrderBoxFragment,
  UserCartTransactionOrderConnectionFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsRight,
  Head,
} from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { StorePurchaseHistorySkeleton } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import {
  Cell,
  CellFrame,
  Organism,
  OrganismFrame,
  Template,
} from '@/core/architecture'

import { FilterDateInputCell } from './FilterDateInputCell'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  artistId: string
  artist: Maybe<
    Pick<Artist, 'id' | 'paymentType' | 'hasArrangingDeliveryItems'>
  >
  orderTransactions: Maybe<UserCartTransactionOrderConnectionFragment>
  sort: OrderBy
  startDate: Date | undefined
  endDate: Date | undefined
  isExporting: boolean
  snackbar: {
    open: boolean
    message: string
    severity: 'success' | 'error'
  }
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onRefetch: (
    orderBy: OrderBy,
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => Promise<void>
  onFetchMore: () => Promise<void>
  onExportCsv: () => Promise<void>
  onCloseSnackbar: () => void
}

type DownloadCsvButtonCellProps = {
  loading?: boolean
  onClick: () => void
}

const DownloadCsvButtonCell: React.FC<DownloadCsvButtonCellProps> = ({
  loading,
  onClick,
}) => {
  const { t } = useTranslation('consoleOrders')
  return (
    <CellFrame>
      <Button
        size="small"
        loadingPosition="start"
        variant="outlined"
        sx={{
          '.MuiButton-loadingIndicator': {
            left: 'auto',
            color: 'primary.main',
          },
          borderRadius: 1,
          height: 28,
          borderColor: 'primary.main',
          color: 'primary.main',
          '&.MuiButton-loading': {
            borderColor: 'grey.100',
          },
          '.MuiButton-startIcon': {
            display: 'none',
          },
        }}
        onClick={onClick}
        loading={loading}
      >
        <Typography variant="subtitle1" color="inherit" pt="2px">
          {loading
            ? t('downloadNow', 'ダウンロード中')
            : t('exportCsv', 'CSVエクスポート')}
        </Typography>
      </Button>
    </CellFrame>
  )
}

export const ConsoleOrdersTemplate: Template<State, Action> = ({
  state,
  onRefetch,
  onFetchMore,
  onExportCsv,
  onCloseSnackbar,
}) => {
  const { t } = useTranslation('consoleOrders')
  const { routerNames } = useRouter()
  const [dateFilterOpen, setDateFilterOpen] = useState(false)

  const handleDateFilterClick = () => {
    setDateFilterOpen(true)
  }

  const handleDateFilterClose = () => {
    setDateFilterOpen(false)
  }
  const handleDateFilterApply = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    onRefetch(state.sort, startDate, endDate)
    setDateFilterOpen(false)
  }

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleOrders} />
      <ConsoleBaseLayout
        margin
        header={{
          left: <ArtistHeaderItemsLeft />,
          right: <ArtistHeaderItemsRight artistId={state.artistId} />,
        }}
      >
        {state.loading && !state.orderTransactions?.edges.length ? (
          <Box sx={{ py: 3 }}>
            <SkeletonText variant="large" width="70%" />
          </Box>
        ) : (
          <>
            <Stack
              direction="row"
              mt={2}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h3" sx={{ py: 3 }}>
                {t('orderManagement')}
              </Typography>
              <DownloadCsvButtonCell
                loading={state.isExporting}
                onClick={onExportCsv}
              />
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ py: 3 }}
            >
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  sx={{
                    borderRadius: 1,
                    height: 28,
                    borderColor:
                      state.startDate && state.endDate
                        ? 'primary.main'
                        : 'grey.400',
                    color:
                      state.startDate && state.endDate ? 'white' : 'grey.400',
                    bgcolor:
                      state.startDate && state.endDate
                        ? 'primary.main'
                        : 'white',
                    '&.MuiButton-loading': {
                      borderColor: 'grey.100',
                    },
                    '.MuiButton-startIcon': {
                      display: 'none',
                    },
                  }}
                  onClick={handleDateFilterClick}
                  endIcon={
                    dateFilterOpen ? (
                      <Icons.UpArrow
                        fontSize="xSmall"
                        sx={{ color: 'inherit' }}
                      />
                    ) : (
                      <Icons.DownArrow
                        fontSize="xSmall"
                        sx={{ color: 'inherit' }}
                      />
                    )
                  }
                >
                  {t('dateFilter', '日付')}
                </Button>
              </Stack>
              <FillterCell
                loading={state.loading}
                orderTransactions={state.orderTransactions}
                onRefetch={onRefetch}
                sort={state.sort}
                startDate={state.startDate}
                endDate={state.endDate}
              />
            </Stack>
            {state.startDate && state.endDate ? (
              <Box
                sx={{
                  py: 1,
                  px: 3,
                  bgcolor: 'primary.light',
                  borderRadius: 1,
                  mb: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    lineHeight: '20px',
                    textAlign: 'center',
                  }}
                >
                  {t('startDate', '開始日')}：
                  {dayjs(state.startDate).format('YYYY/MM/DD')} {' ～ '}
                  {t('endDate', '終了日')}：
                  {dayjs(state.endDate).format('YYYY/MM/DD')}
                </Typography>
              </Box>
            ) : (
              <></>
            )}
          </>
        )}
        <ScrollFetcher
          nodes={state.orderTransactions?.edges.map(({ node }) => (
            <OrderItem
              key={node.id}
              order={node}
              artistPaymentType={state.artist?.paymentType}
            />
          ))}
          loadingComponent={
            <Stack spacing={5} sx={{ pt: 2 }}>
              {Array.from(Array(2).keys()).map(key => (
                <StorePurchaseHistorySkeleton key={key} />
              ))}
            </Stack>
          }
          componentProps={{ spacing: 5 }}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          emptyMessage={t('ordersEmpty')}
        />

        <Drawer
          anchor="bottom"
          open={dateFilterOpen}
          onClose={handleDateFilterClose}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '80vh',
            },
          }}
        >
          <FilterDateInputCell
            startDate={state.startDate}
            endDate={state.endDate}
            onClose={handleDateFilterClose}
            onApply={(startDate, endDate) =>
              handleDateFilterApply(startDate, endDate)
            }
          />
        </Drawer>

        <Snackbar
          open={state.snackbar.open}
          autoHideDuration={6000}
          onClose={onCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={onCloseSnackbar}
            severity={state.snackbar.severity}
            sx={{ width: '100%' }}
          >
            {state.snackbar.message}
          </Alert>
        </Snackbar>
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type FillterCellProps = {
  loading: State['loading']
  orderTransactions: State['orderTransactions']
  onRefetch: Action['onRefetch']
  sort: OrderBy
  startDate?: Date
  endDate?: Date
}
export const FillterCell: Cell<FillterCellProps> = ({
  loading,
  orderTransactions,
  onRefetch,
  sort,
  startDate,
  endDate,
}) => {
  const { t } = useTranslation('consoleOrders')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(
    sort === OrderBy.Desc ? 0 : 1
  )

  const options: { label: string; value: OrderBy }[] = [
    { label: t('newestOrderDate', '注文日の新しい順'), value: OrderBy.Desc },
    { label: t('oldestOrderDate', '注文日の古い順'), value: OrderBy.Asc },
  ]

  const handleFilterClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuItemClick = useCallback(
    (event: MouseEvent<HTMLElement>, newIndex: number) => {
      setSelectedIndex(newIndex)
      setAnchorEl(null)
      onRefetch(options[newIndex].value, startDate, endDate)
    },
    [onRefetch, options, startDate, endDate]
  )
  useEffect(() => {
    const UrlParams = `?sort=${options[selectedIndex].value}`
    // React Routerのhistoryを使うとソート順を変えた際に、スクロール位置が一番上になるため、JavaScriptで直接historyを利用している
    window.history.replaceState(null, '', UrlParams)
  }, [selectedIndex])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  if (loading && !orderTransactions?.edges.length) {
    return (
      <Stack alignItems="flex-end" sx={{ pb: 3 }}>
        <SkeletonText variant="small" width="30%" />
      </Stack>
    )
  }
  return (
    <CellFrame>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        sx={{ pb: 3 }}
      >
        <Button
          variant="text"
          sx={{ width: 'fit-content', mr: -2 }}
          onClick={handleFilterClick}
        >
          <Typography variant="caption" sx={{ pr: 1 }}>
            {options[selectedIndex]?.label}
          </Typography>
          <Icons.DownArrow fontSize="xSmall" sx={{ color: 'grey.600' }} />
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {options.map((option, index) => (
            <MenuItem
              sx={{ bgcolor: 'grey.50', minWidth: 200 }}
              key={index}
              selected={index === selectedIndex}
              onClick={event => handleMenuItemClick(event, index)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
    </CellFrame>
  )
}

type OrderItemProps = {
  order: UserCartTransactionOrderBoxFragment
  artistPaymentType: Artist['paymentType']
}
export const OrderItem: Organism<OrderItemProps> = ({
  order,
  artistPaymentType,
}) => {
  const { t } = useTranslation('consoleOrders')
  const { generatePath, routerPages } = useRouter()

  const pickupUser = order.pickupUsers?.[0]

  return (
    <OrganismFrame>
      <Card sx={{ borderRadius: 2 }}>
        <CardActionArea
          href={generatePath(routerPages.ConsoleOrderDetail, {
            orderId: order.id,
            userId: order.itemOrders?.[0]?.user?.id ?? '#',
          })}
        >
          <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                {!!order.cancels?.length && (
                  <Chip
                    color="error"
                    label={
                      <Typography variant="overline" color="white">
                        {t('refundAvailable', '返金対応')}
                      </Typography>
                    }
                    size="small"
                    sx={{ height: 18 }}
                  />
                )}
                <Typography variant="h6">
                  {order.itemOrders?.[0]?.user?.fullName}
                </Typography>
              </Stack>
              <Typography variant="body2">
                {t('orderDate', { date: order.formattedCreatedAt })}
              </Typography>
            </Stack>
            {!!pickupUser?.formattedPickupStartEndTime && (
              <Stack spacing={1} sx={{ pt: 3 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t('pickupTime', '受け取り時間')}
                </Typography>
                <Typography variant="subtitle1">
                  {pickupUser.formattedPickupStartEndTime}
                </Typography>
              </Stack>
            )}
            <Stack spacing={1} sx={{ pt: 3 }}>
              <Typography variant="subtitle1" color="text.secondary">
                {t('orderDetails')}
              </Typography>
              {order.items?.map((item, index) => {
                if (item.skus.length) {
                  return item.skus.map(
                    sku =>
                      sku && (
                        <Stack
                          key={sku.skuId}
                          spacing={2}
                          direction="row"
                          justifyContent="space-between"
                        >
                          <Stack flex={1} sx={{ minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              sx={styleSets.lineCamp(1)}
                            >
                              {item.item.title} ({sku.sku.unitName})
                            </Typography>
                          </Stack>
                          <Typography variant="subtitle1">
                            {t('quantity', { quantity: sku.count })}
                          </Typography>
                        </Stack>
                      )
                  )
                }
                return (
                  <Stack
                    key={index}
                    spacing={2}
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Stack flex={1} sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        sx={styleSets.lineCamp(1)}
                      >
                        {item.item.title}
                      </Typography>
                    </Stack>
                    <Typography variant="subtitle1">
                      {t('quantity', { quantity: item.count })}
                    </Typography>
                  </Stack>
                )
              })}
            </Stack>
            <Stack spacing={1} sx={{ pt: 3 }}>
              <Typography variant="subtitle1" color="text.secondary">
                {order.paymentType === UserPaymentType.Cash
                  ? t('payment')
                  : t('totalPoint')}
              </Typography>
              {order.paymentType === UserPaymentType.Cash ? (
                <Typography variant="subtitle1">
                  ￥{order.formattedTotalAmount}
                </Typography>
              ) : (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Icons.Point fontSize="small" />
                  <Typography variant="subtitle1">
                    {order.formattedTotalAmount}
                  </Typography>
                </Stack>
              )}
            </Stack>
            {!!order.formattedSaleProfit &&
              order.paymentType === UserPaymentType.Cash &&
              artistPaymentType === ArtistPaymentType.Connect && (
                <Stack spacing={1} sx={{ pt: 3 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('saleProfit', '販売利益')}
                  </Typography>
                  <Typography variant="subtitle1">
                    ￥{order.formattedSaleProfit}
                  </Typography>
                </Stack>
              )}
          </CardContent>
        </CardActionArea>
      </Card>
    </OrganismFrame>
  )
}
